  // FeatureDemoThumb.jsx
// Scales one of the animated demo components down to fit a small thumbnail
// slot (e.g. the Features grid cards), instead of a static screenshot.
//
// The wrapped Demo keeps rendering at its natural size; this wrapper just
// shrinks the whole thing via CSS transform so it fits the measured height
// of its container, cropping any overflow.
//
// Usage: <FeatureDemoThumb demo={SomeDemo} naturalHeight={470} demoProps={{ height: 340 }} />
//
// Each demo is a small animated app (hundreds of elements plus timers), so it
// is only mounted while its card is on or near the screen. Prerendered
// snapshots always get the empty placeholder, which keeps hydration matching.

import { useEffect, useRef, useState } from "react";
import { isReactSnap } from "../utils/snapshot";

export default function FeatureDemoThumb({ demo: Demo, naturalHeight, demoProps }) {
  const outerRef = useRef(null);
  const [scale, setScale] = useState(0.5);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = outerRef.current;
    if (!el || isReactSnap()) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin: "200px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.clientHeight;
      if (h > 0) setScale(h / naturalHeight);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [naturalHeight]);

  return (
    <div className="feature-demo-thumb" ref={outerRef} style={{ width: "100%", height: "100%", overflow: "hidden", position: "relative", pointerEvents: "none" }}>
      <style>{`
        .feature-demo-thumb div:has(> svg > path[d="M4 2 L4 20 L9 15 L12.5 22 L15 21 L11.5 14 L18 14 Z"]) {
          display: none !important;
        }
      `}</style>
      {inView && (
        <div style={{ width: `${100 / scale}%`, transform: `scale(${scale})`, transformOrigin: "top left" }}>
          <Demo {...demoProps} />
        </div>
      )}
    </div>
  );
}
